import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  PlusCircle,
  Building2,
  CreditCard,
  CheckCircle2,
  Trash2,
  Edit3,
  Star,
  AlertCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
  accountType: "savings" | "current";
  isDefault: boolean;
  isVerified: boolean;
}

const initialBanks: BankAccount[] = [
  {
    id: "BANK-001",
    bankName: "First Bank of Nigeria",
    accountNumber: "0123456789",
    accountName: "John Doe Trading",
    accountType: "current",
    isDefault: true,
    isVerified: true,
  },
  {
    id: "BANK-002",
    bankName: "Guaranty Trust Bank",
    accountNumber: "0987654321",
    accountName: "John Doe Trading",
    accountType: "savings",
    isDefault: false,
    isVerified: true,
  },
];

const nigerianBanks = [
  "First Bank of Nigeria",
  "Guaranty Trust Bank",
  "Access Bank",
  "United Bank for Africa (UBA)",
  "Zenith Bank",
  "Fidelity Bank",
  "Union Bank",
  "Ecobank",
  "Stanbic IBTC",
  "Polaris Bank",
  "Wema Bank",
  "Sterling Bank",
  "Keystone Bank",
  "Unity Bank",
  "Jaiz Bank",
];

export default function VendorBanks() {
  const [banks, setBanks] = useState<BankAccount[]>(initialBanks);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState<BankAccount | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    bankName: "",
    accountNumber: "",
    accountName: "",
    accountType: "current" as "savings" | "current",
  });

  const handleAddBank = () => {
    if (
      !formData.bankName ||
      !formData.accountNumber ||
      !formData.accountName
    ) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    const newBank: BankAccount = {
      id: `BANK-${Date.now()}`,
      bankName: formData.bankName,
      accountNumber: formData.accountNumber,
      accountName: formData.accountName,
      accountType: formData.accountType,
      isDefault: banks.length === 0,
      isVerified: false,
    };

    setBanks([...banks, newBank]);
    setFormData({
      bankName: "",
      accountNumber: "",
      accountName: "",
      accountType: "current",
    });
    setIsAddDialogOpen(false);

    toast({
      title: "Bank Account Added",
      description:
        "Your bank account has been added and is pending verification.",
    });
  };

  const handleEditBank = () => {
    if (!selectedBank) return;

    setBanks(
      banks.map((bank) =>
        bank.id === selectedBank.id ? { ...bank, ...formData } : bank,
      ),
    );
    setIsEditDialogOpen(false);
    setSelectedBank(null);

    toast({
      title: "Bank Account Updated",
      description: "Your bank account details have been updated.",
    });
  };

  const handleDeleteBank = (bankId: string) => {
    setBanks(banks.filter((bank) => bank.id !== bankId));
    toast({
      title: "Bank Account Removed",
      description: "The bank account has been removed from your profile.",
    });
  };

  const handleSetDefault = (bankId: string) => {
    setBanks(
      banks.map((bank) => ({
        ...bank,
        isDefault: bank.id === bankId,
      })),
    );
    toast({
      title: "Default Account Updated",
      description: "Your default bank account has been updated.",
    });
  };

  const openEditDialog = (bank: BankAccount) => {
    setSelectedBank(bank);
    setFormData({
      bankName: bank.bankName,
      accountNumber: bank.accountNumber,
      accountName: bank.accountName,
      accountType: bank.accountType,
    });
    setIsEditDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Bank Accounts</h1>
          <p className="text-muted-foreground">
            Manage your withdrawal bank accounts
          </p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-500 hover:bg-emerald-600">
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Bank Account
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Bank Account</DialogTitle>
              <DialogDescription>
                Add a new bank account for withdrawals
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="bankName">Bank Name</Label>
                <select
                  id="bankName"
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  value={formData.bankName}
                  onChange={(e) =>
                    setFormData({ ...formData, bankName: e.target.value })
                  }
                >
                  <option value="">Select Bank</option>
                  {nigerianBanks.map((bank) => (
                    <option key={bank} value={bank}>
                      {bank}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="accountNumber">Account Number</Label>
                <Input
                  id="accountNumber"
                  placeholder="10-digit account number"
                  maxLength={10}
                  value={formData.accountNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, accountNumber: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="accountName">Account Name</Label>
                <Input
                  id="accountName"
                  placeholder="Full account name"
                  value={formData.accountName}
                  onChange={(e) =>
                    setFormData({ ...formData, accountName: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Account Type</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="accountType"
                      value="current"
                      checked={formData.accountType === "current"}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          accountType: e.target.value as "current" | "savings",
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span>Current</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="accountType"
                      value="savings"
                      checked={formData.accountType === "savings"}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          accountType: e.target.value as "current" | "savings",
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span>Savings</span>
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddBank}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                Add Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Bank Accounts List */}
      <div className="grid gap-4">
        {banks.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Bank Accounts</h3>
              <p className="text-muted-foreground mb-4">
                You haven't added any bank accounts yet. Add one to receive
                withdrawals.
              </p>
              <Button
                onClick={() => setIsAddDialogOpen(true)}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Bank Account
              </Button>
            </CardContent>
          </Card>
        ) : (
          banks.map((bank) => (
            <Card
              key={bank.id}
              className={cn(bank.isDefault && "border-emerald-500/30")}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-emerald-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{bank.bankName}</p>
                        {bank.isDefault && (
                          <Badge className="bg-emerald-500/20 text-emerald-500 text-[10px]">
                            <Star className="h-3 w-3 mr-1" />
                            Default
                          </Badge>
                        )}
                        {bank.isVerified ? (
                          <Badge className="bg-emerald-500/20 text-emerald-500 text-[10px]">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Verified
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/20 text-amber-500 text-[10px]">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {bank.accountType === "current"
                          ? "Current Account"
                          : "Savings Account"}
                      </p>
                      <p className="text-sm font-mono mt-1">
                        **** **** **** {bank.accountNumber.slice(-4)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {bank.accountName}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!bank.isDefault && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSetDefault(bank.id)}
                      >
                        <Star className="h-4 w-4 mr-2" />
                        Set Default
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(bank)}
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => handleDeleteBank(bank.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Bank Account</DialogTitle>
            <DialogDescription>
              Update your bank account details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editBankName">Bank Name</Label>
              <select
                id="editBankName"
                className="w-full px-3 py-2 border rounded-md bg-background"
                value={formData.bankName}
                onChange={(e) =>
                  setFormData({ ...formData, bankName: e.target.value })
                }
              >
                <option value="">Select Bank</option>
                {nigerianBanks.map((bank) => (
                  <option key={bank} value={bank}>
                    {bank}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="editAccountNumber">Account Number</Label>
              <Input
                id="editAccountNumber"
                placeholder="10-digit account number"
                maxLength={10}
                value={formData.accountNumber}
                onChange={(e) =>
                  setFormData({ ...formData, accountNumber: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editAccountName">Account Name</Label>
              <Input
                id="editAccountName"
                placeholder="Full account name"
                value={formData.accountName}
                onChange={(e) =>
                  setFormData({ ...formData, accountName: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditBank}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
